import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Stack, Box, TextField, MenuItem,
  Checkbox, Avatar, Chip, Alert, CircularProgress,
  InputAdornment, Divider, LinearProgress, IconButton,
} from '@mui/material';
import {
  Close, Search, FitnessCenter, Restaurant, CheckCircle,
  PersonAdd, AssignmentTurnedIn,
} from '@mui/icons-material';
import { usersAPI, workoutPlansAPI, mealPlansAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';

/**
 * AssignPlanDialog
 *
 * Props:
 *   open          � boolean
 *   onClose       � () => void
 *   planType      � 'workout' | 'meal'
 *   preselectedPlan � plan object (optional) � when opened from a plan card
 *   preselectedUsers � array of user objects (optional) � when opened from Users page
 */
export default function AssignPlanDialog({ open, onClose, planType: initialPlanType, preselectedPlan, preselectedUsers }) {
  const [step, setStep] = useState(0); // 0=select plan, 1=select users, 2=confirm/result
  const [planType, setPlanType] = useState(initialPlanType || 'workout');
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(preselectedPlan || null);
  const [selectedUsers, setSelectedUsers] = useState(preselectedUsers || []);
  const [planSearch, setPlanSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // If a plan is preselected, skip to user selection
  const startStep = preselectedPlan ? 1 : 0;

  useEffect(() => {
    if (!open) return;
    setStep(startStep);
    setSelectedPlan(preselectedPlan || null);
    setSelectedUsers(preselectedUsers || []);
    setResult(null);
    setError('');
    setPlanSearch('');
    setUserSearch('');
    if (initialPlanType) setPlanType(initialPlanType);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load plans when on step 0
  useEffect(() => {
    if (!open || step !== 0) return;
    setLoading(true);
    const api = planType === 'workout' ? workoutPlansAPI : mealPlansAPI;
    api.getAll()
      .then(r => setPlans(r.data.data || []))
      .catch(() => setError('Failed to load plans'))
      .finally(() => setLoading(false));
  }, [open, step, planType]);

  // Load users when on step 1
  useEffect(() => {
    if (!open || step !== 1 || preselectedUsers) return;
    setLoading(true);
    usersAPI.getAll({ role: 'user' })
      .then(r => setUsers(r.data.data || []))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleUser = (user) => {
    setSelectedUsers(prev =>
      prev.find(u => u._id === user._id)
        ? prev.filter(u => u._id !== user._id)
        : [...prev, user]
    );
  };

  const selectAllFiltered = () => {
    const filtered = filteredUsers();
    const allSelected = filtered.every(u => selectedUsers.find(s => s._id === u._id));
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(u => !filtered.find(f => f._id === u._id)));
    } else {
      const toAdd = filtered.filter(u => !selectedUsers.find(s => s._id === u._id));
      setSelectedUsers(prev => [...prev, ...toAdd]);
    }
  };

  const filteredPlans = () => plans.filter(p =>
    !planSearch || p.name.toLowerCase().includes(planSearch.toLowerCase())
  );

  const filteredUsers = () => {
    const source = preselectedUsers || users;
    return source.filter(u =>
      !userSearch ||
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase())
    );
  };

  const handleAssign = async () => {
    if (!selectedPlan || selectedUsers.length === 0) return;
    setAssigning(true);
    setError('');
    try {
      const api = planType === 'workout' ? workoutPlansAPI : mealPlansAPI;
      const res = await api.assign({
        planType,
        planId: selectedPlan._id,
        userIds: selectedUsers.map(u => u._id),
      });
      setResult(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const planIcon = planType === 'workout'
    ? <FitnessCenter sx={{ color: '#1a1a2ed4' }} />
    : <Restaurant sx={{ color: '#FF6B35' }} />;

  const planColor = planType === 'workout' ? '#1a1a2ed4' : '#FF6B35';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1, bgcolor: `${planColor}18`, borderRadius: 1.5, display: 'flex' }}>
            {planIcon}
          </Box>
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>Assign Plan to Users</Typography>
            <Typography variant="caption" color="text.secondary">
              {step === 0 ? 'Step 1: Select a plan' : step === 1 ? 'Step 2: Select users' : 'Done'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={step === 0 ? 33 : step === 1 ? 66 : 100}
          sx={{ mt: 1.5, borderRadius: 1, bgcolor: `${planColor}18`, '& .MuiLinearProgress-bar': { bgcolor: planColor } }}
        />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, minHeight: 360 }}>
        {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* -- Step 0: Select Plan --------------------------------------- */}
        {step === 0 && (
          <Box>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} mb={1.5}>
                <Button
                  size="small" variant={planType === 'workout' ? 'contained' : 'outlined'}
                  startIcon={<FitnessCenter />}
                  onClick={() => setPlanType('workout')}
                  sx={planType === 'workout' ? { bgcolor: '#1a1a2ed4', '&:hover': { bgcolor: '#5850E6' } } : {}}
                >
                  Workout
                </Button>
                <Button
                  size="small" variant={planType === 'meal' ? 'contained' : 'outlined'}
                  startIcon={<Restaurant />}
                  onClick={() => setPlanType('meal')}
                  sx={planType === 'meal' ? { bgcolor: '#FF6B35', '&:hover': { bgcolor: '#E55A25' } } : {}}
                >
                  Meal
                </Button>
              </Stack>
              <TextField
                placeholder="Search plans..."
                value={planSearch}
                onChange={e => setPlanSearch(e.target.value)}
                size="small" fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {filteredPlans().map(plan => (
                  <Box
                    key={plan._id}
                    onClick={() => setSelectedPlan(plan)}
                    sx={{
                      p: 2, cursor: 'pointer', borderBottom: 1, borderColor: 'divider',
                      bgcolor: selectedPlan?._id === plan._id ? `${planColor}08` : 'transparent',
                      borderLeft: selectedPlan?._id === plan._id ? `3px solid ${planColor}` : '3px solid transparent',
                      '&:hover': { bgcolor: `${planColor}06` },
                      transition: 'all 0.15s',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Checkbox
                        checked={selectedPlan?._id === plan._id}
                        size="small"
                        sx={{ p: 0, color: planColor, '&.Mui-checked': { color: planColor } }}
                      />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600}>{plan.name}</Typography>
                        <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                          <Chip label={plan.difficulty || plan.goal || '�'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                          <Chip label={`${plan.durationWeeks}w`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                          <Chip label={plan.isFree ? 'Free' : formatCurrency(plan.price)} size="small" color={plan.isFree ? 'success' : 'warning'} sx={{ height: 18, fontSize: '0.65rem' }} />
                          {!plan.isPublished && <Chip label="Draft" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="text.secondary">{plan.enrolledCount || 0} enrolled</Typography>
                    </Stack>
                  </Box>
                ))}
                {filteredPlans().length === 0 && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="text.secondary">No plans found</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* -- Step 1: Select Users -------------------------------------- */}
        {step === 1 && (
          <Box>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              {/* Selected plan summary */}
              {selectedPlan && (
                <Box sx={{ p: 1.5, mb: 1.5, bgcolor: `${planColor}08`, borderRadius: 1.5, border: `1px solid ${planColor}22` }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {planIcon}
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={700}>{selectedPlan.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{planType} plan � {selectedPlan.durationWeeks}w</Typography>
                    </Box>
                    {!preselectedPlan && (
                      <Button size="small" onClick={() => setStep(0)} sx={{ fontSize: '0.7rem' }}>Change</Button>
                    )}
                  </Stack>
                </Box>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  size="small" sx={{ flex: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                />
                <Button size="small" variant="outlined" onClick={selectAllFiltered} sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}>
                  {filteredUsers().every(u => selectedUsers.find(s => s._id === u._id)) ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>
              {selectedUsers.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" color={planColor} fontWeight={700}>
                    {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                  </Typography>
                </Box>
              )}
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {filteredUsers().map(user => {
                  const checked = !!selectedUsers.find(u => u._id === user._id);
                  return (
                    <Box
                      key={user._id}
                      onClick={() => toggleUser(user)}
                      sx={{
                        p: 1.5, cursor: 'pointer', borderBottom: 1, borderColor: 'divider',
                        bgcolor: checked ? `${planColor}08` : 'transparent',
                        borderLeft: checked ? `3px solid ${planColor}` : '3px solid transparent',
                        '&:hover': { bgcolor: `${planColor}06` },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Checkbox
                          checked={checked} size="small"
                          sx={{ p: 0, color: planColor, '&.Mui-checked': { color: planColor } }}
                        />
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700, bgcolor: planColor }}>
                          {user.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        </Box>
                        <Chip
                          label={user.membershipStatus || 'inactive'}
                          size="small"
                          color={user.membershipStatus === 'active' ? 'success' : 'default'}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
                {filteredUsers().length === 0 && (
                  <Box textAlign="center" py={4}>
                    <PersonAdd sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No users found</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* -- Step 2: Result -------------------------------------------- */}
        {step === 2 && result && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} px={3}>
            <AssignmentTurnedIn sx={{ fontSize: 56, color: planColor, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>Assignment Complete</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
              {result.message}
            </Typography>
            <Stack spacing={1} width="100%">
              {result.data?.assigned?.length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#2ED57318', borderRadius: 1.5, border: '1px solid #2ED57344' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircle sx={{ color: '#2ED573', fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600} color="#2ED573">
                      {result.data.assigned.length} user{result.data.assigned.length > 1 ? 's' : ''} assigned successfully
                    </Typography>
                  </Stack>
                </Box>
              )}
              {result.data?.alreadyEnrolled?.length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#FF9F4318', borderRadius: 1.5, border: '1px solid #FF9F4344' }}>
                  <Typography variant="body2" fontWeight={600} color="#FF9F43">
                    {result.data.alreadyEnrolled.length} re-activated (were already enrolled)
                  </Typography>
                </Box>
              )}
              {result.data?.failed?.length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#FF475718', borderRadius: 1.5, border: '1px solid #FF475744' }}>
                  <Typography variant="body2" fontWeight={600} color="#FF4757">
                    {result.data.failed.length} failed
                  </Typography>
                </Box>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" mt={2} textAlign="center">
              The plan will appear as the active plan in each user's app on their next sync.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 2 ? (
          <Button variant="contained" onClick={onClose} sx={{ bgcolor: planColor, '&:hover': { bgcolor: planColor } }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={step === 0 || (step === 1 && preselectedPlan) ? onClose : () => setStep(s => s - 1)}>
              {step === 0 || (step === 1 && preselectedPlan) ? 'Cancel' : 'Back'}
            </Button>
            {step === 0 && (
              <Button
                variant="contained"
                disabled={!selectedPlan}
                onClick={() => setStep(1)}
                sx={{ bgcolor: planColor, '&:hover': { bgcolor: planColor } }}
              >
                Next: Select Users
              </Button>
            )}
            {step === 1 && (
              <Button
                variant="contained"
                disabled={selectedUsers.length === 0 || assigning}
                onClick={handleAssign}
                startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <AssignmentTurnedIn />}
                sx={{ bgcolor: planColor, '&:hover': { bgcolor: planColor } }}
              >
                {assigning ? 'Assigning...' : `Assign to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
